import React, { useState, useRef, useEffect } from 'react';
import { commentService } from '../services/comments';

interface CommentInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'set' | 'workout' | 'exercise';
  maxLength?: number;
  showQuickPhrases?: boolean;
  context?: {
    exerciseType?: string;
    weight?: number;
    reps?: number;
    previousComments?: string[];
    workoutDuration?: number;
  };
}

/**
 * CommentInput - Component for entering comments with quick phrase selection
 * Supports Russian text input and provides contextual suggestions
 * Requirements: 9.3, 9.4, 12.1, 12.2
 */
export const CommentInput: React.FC<CommentInputProps> = ({
  value,
  onChange,
  placeholder = 'Добавить комментарий...',
  type = 'set',
  maxLength = 500,
  showQuickPhrases = true,
  context
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Get suggestions based on search query and context
  const getSuggestions = () => {
    if (context) {
      const contextSuggestions = commentService.generateSuggestions(context);
      if (searchQuery) {
        const searchResults = commentService.searchQuickPhrases(searchQuery, type);
        return [...new Set([...contextSuggestions, ...searchResults])].slice(0, 8);
      }
      return contextSuggestions;
    }
    
    return commentService.searchQuickPhrases(searchQuery, type === 'exercise' ? 'set' : type);
  };

  const suggestions = getSuggestions();

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    
    // Validate input
    const validation = commentService.validateComment(newValue);
    setValidationErrors(validation.errors);
    
    onChange(newValue);
    
    // Update search query for suggestions
    const words = newValue.split(' ');
    const lastWord = words[words.length - 1];
    setSearchQuery(lastWord);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: string) => {
    const formatted = commentService.formatComment(suggestion);
    onChange(formatted);
    setShowSuggestions(false);
    setSearchQuery('');
    
    // Focus back to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle key navigation in suggestions
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const remainingChars = maxLength - value.length;
  const isNearLimit = remainingChars < 50;
  const isOverLimit = remainingChars < 0;

  return (
    <div className="comment-input relative">
      {/* Input Field */}
      <div className="relative">
        <textarea
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(showQuickPhrases)}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border rounded-md resize-none focus:outline-none focus:ring-2 transition-colors ${
            isOverLimit
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : validationErrors.length > 0
              ? 'border-yellow-300 focus:ring-yellow-500 focus:border-yellow-500'
              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
          }`}
          rows={2}
          maxLength={maxLength}
        />
        
        {/* Character Counter */}
        <div className={`absolute bottom-2 right-2 text-xs ${
          isOverLimit ? 'text-red-500' : isNearLimit ? 'text-yellow-500' : 'text-gray-400'
        }`}>
          {remainingChars}
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="mt-1 text-sm text-yellow-600">
          {validationErrors.map((error, index) => (
            <div key={index}>• {error}</div>
          ))}
        </div>
      )}

      {/* Quick Phrases Suggestions */}
      {showSuggestions && showQuickPhrases && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          <div className="p-2 text-xs text-gray-500 border-b border-gray-100">
            {context ? 'Рекомендации:' : 'Быстрые фразы:'}
          </div>
          
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionSelect(suggestion)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors"
            >
              {suggestion}
            </button>
          ))}
          
          {/* Show all phrases button */}
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                setShowSuggestions(true);
              }}
              className="w-full px-3 py-2 text-left text-xs text-blue-600 hover:bg-blue-50 border-t border-gray-100"
            >
              Показать все фразы
            </button>
          )}
        </div>
      )}

      {/* Quick Phrases Toggle Button */}
      {!showSuggestions && showQuickPhrases && (
        <button
          onClick={() => setShowSuggestions(true)}
          className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1l-4 4z" />
          </svg>
          Быстрые фразы
        </button>
      )}

      {/* Russian Input Indicator */}
      {value && commentService.isRussianText(value) && (
        <div className="mt-1 text-xs text-green-600 flex items-center">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Русский текст
        </div>
      )}
    </div>
  );
};

export default CommentInput;